import React, { useState, useRef } from 'react';
import { Upload, CheckCircle, FileSpreadsheet, Database, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' },
  cardTitle: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.1rem', marginBottom: '1rem' },
  demoBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20,
    background: 'rgba(200,169,110,0.15)', color: 'var(--secondary)', fontFamily: 'DM Sans, sans-serif',
    fontSize: '0.8rem', fontWeight: 600, marginBottom: '1.5rem',
  },
  dropzone: (dragging) => ({
    border: `2px dashed ${dragging ? 'var(--primary)' : 'rgba(242,237,228,0.2)'}`,
    borderRadius: 12, padding: '2.5rem', textAlign: 'center', cursor: 'pointer',
    background: dragging ? 'rgba(141,184,122,0.05)' : 'transparent',
    transition: 'all 0.2s',
  }),
  dropLabel: { fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.95rem', marginTop: '0.75rem' },
  dropSub: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.8rem', marginTop: 4 },
  typeButtons: { display: 'flex', gap: 8, justifyContent: 'center', marginBottom: '1rem' },
  typeBtn: (active) => ({
    fontFamily: 'DM Sans, sans-serif', padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: '0.82rem', fontWeight: 500,
    background: active ? 'var(--primary)' : 'rgba(242,237,228,0.08)',
    color: active ? 'var(--bg)' : 'var(--text)',
  }),
  mapperGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  mapperRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderRadius: 8, background: 'rgba(242,237,228,0.04)' },
  mapperLabel: { fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: 'var(--text)' },
  mapperArrow: { color: 'var(--secondary)', margin: '0 8px', fontSize: '0.8rem' },
  healthRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.9rem' },
  healthCheck: { color: 'var(--primary)', flexShrink: 0 },
};

const toastFields = [
  ['order_id', 'transaction_id'],
  ['customer_email', 'email'],
  ['order_total', 'amount'],
  ['order_date', 'transaction_date'],
  ['items', 'line_items'],
  ['location', 'store_id'],
];

const klaviyoFields = [
  ['email', 'email'],
  ['first_name', 'first_name'],
  ['last_name', 'last_name'],
  ['$consent', 'push_opt_in'],
  ['list_id', 'segment'],
  ['campaign_id', 'campaign_name'],
];

const healthChecks = [
  'Customer records: 1,200 loaded',
  'Transaction history: 18 months',
  'No duplicate records detected',
  'All email addresses validated',
  'Store IDs mapped to 6 locations',
  'Loyalty tiers assigned to all customers',
  'Push opt-in status synced from Klaviyo',
];

export default function DataManager() {
  const [dragging, setDragging] = useState(false);
  const [uploadType, setUploadType] = useState('Customers');
  const fileRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const handleUpload = async (file) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', uploadType);
    try {
      await api.post('/data/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${uploadType} data uploaded successfully`);
    } catch {
      toast.success(`${uploadType} data uploaded (demo mode)`);
    }
  };

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Data Manager</h1>
      <p style={s.desc}>Upload, map, and validate your customer data. Connects to Toast POS and Klaviyo.</p>

      <div style={s.demoBadge}><Zap size={14} /> Demo Mode — Sample data pre-loaded</div>

      <div style={s.card}>
        <div style={s.cardTitle}>Upload CSV</div>
        <div style={s.typeButtons}>
          {['Customers', 'Transactions', 'Campaigns'].map(t => (
            <button key={t} style={s.typeBtn(uploadType === t)} onClick={() => setUploadType(t)}>{t}</button>
          ))}
        </div>
        <div
          style={s.dropzone(dragging)}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={32} style={{ color: 'var(--secondary)' }} />
          <div style={s.dropLabel}>Drop a CSV file here, or click to browse</div>
          <div style={s.dropSub}>Supports .csv files up to 10MB</div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleUpload(e.target.files[0])} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <div style={s.card}>
          <div style={s.cardTitle}><FileSpreadsheet size={16} style={{ marginRight: 8, verticalAlign: -2 }} />Toast POS Field Map</div>
          {toastFields.map(([from, to]) => (
            <div key={from} style={s.mapperRow}>
              <span style={s.mapperLabel}>{from}</span>
              <span style={s.mapperArrow}>→</span>
              <span style={{ ...s.mapperLabel, color: 'var(--primary)' }}>{to}</span>
            </div>
          ))}
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}><Database size={16} style={{ marginRight: 8, verticalAlign: -2 }} />Klaviyo Field Map</div>
          {klaviyoFields.map(([from, to]) => (
            <div key={from} style={s.mapperRow}>
              <span style={s.mapperLabel}>{from}</span>
              <span style={s.mapperArrow}>→</span>
              <span style={{ ...s.mapperLabel, color: 'var(--primary)' }}>{to}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>Data Health</div>
        {healthChecks.map((check, i) => (
          <div key={i} style={s.healthRow}>
            <CheckCircle size={16} style={s.healthCheck} />
            {check}
          </div>
        ))}
      </div>
    </div>
  );
}
